import { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Form, Button, Alert, Image } from 'react-bootstrap';
import { useBranding } from '@context';

export function BrandingPage() {
  const { branding, loading, updateBranding, resetBranding } = useBranding();

  const [appName, setAppName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [colors, setColors] = useState({
    primary: '#0d6efd',
    secondary: '#6c757d',
    success: '#198754',
    danger: '#dc3545',
    warning: '#ffc107',
    info: '#0dcaf0',
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!branding) return;
    setAppName(branding.appName || 'Adaptive Learning');
    setLogoUrl(branding.logoUrl || '');
    setColors(prev => ({
      ...prev,
      ...(branding.colors || {}),
    }));
  }, [branding]);

  const colorFields = useMemo(() => ([
    { key: 'primary', label: 'Primary' },
    { key: 'secondary', label: 'Secondary' },
    { key: 'success', label: 'Success' },
    { key: 'danger', label: 'Danger' },
    { key: 'warning', label: 'Warning' },
    { key: 'info', label: 'Info' },
  ]), []);

  const handleColorChange = (key, value) => {
    setColors(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    updateBranding({ appName, logoUrl, colors });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleReset = () => {
    resetBranding();
    setSaved(false);
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setLogoUrl(typeof reader.result === 'string' ? reader.result : '');
    };
    reader.readAsDataURL(file);
  };

  if (loading) {
    return <div className="text-center py-5">Loading branding settings...</div>;
  }

  return (
    <div>
      <div className="mb-4">
        <h4 className="mb-1">🎨 Branding & Theme</h4>
        <small className="text-muted">Update app logo and global color palette.</small>
      </div>

      {saved && (
        <Alert variant="success" className="mb-3">
          Branding updated successfully.
        </Alert>
      )}

      <Card className="mb-4">
        <Card.Header>
          <strong>Brand</strong>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Application Name</Form.Label>
                <Form.Control
                  type="text"
                  value={appName}
                  onChange={(e) => setAppName(e.target.value)}
                  placeholder="Adaptive Learning"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Logo URL</Form.Label>
                <Form.Control
                  type="url"
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  placeholder="https://example.com/logo.png"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Or Upload Logo</Form.Label>
                <Form.Control type="file" accept="image/*" onChange={handleLogoUpload} />
              </Form.Group>
            </Col>
            <Col md={6} className="d-flex align-items-end">
              <div>
                <div className="text-muted small mb-2">Preview</div>
                {logoUrl ? (
                  <Image src={logoUrl} alt="Logo preview" rounded style={{ maxHeight: 48, maxWidth: 180 }} />
                ) : (
                  <span className="text-muted small">No logo selected</span>
                )}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <strong>Theme Colors</strong>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            {colorFields.map(({ key, label }) => (
              <Col key={key} md={4}>
                <Form.Group>
                  <Form.Label>{label}</Form.Label>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Control
                      type="color"
                      value={colors[key]}
                      onChange={(e) => handleColorChange(key, e.target.value)}
                      style={{ width: 54, height: 38, padding: 4 }}
                    />
                    <Form.Control
                      type="text"
                      value={colors[key]}
                      onChange={(e) => handleColorChange(key, e.target.value)}
                    />
                  </div>
                </Form.Group>
              </Col>
            ))}
          </Row>

          <div className="d-flex gap-2 mt-4">
            <Button variant="primary" onClick={handleSave}>Save Branding</Button>
            <Button variant="outline-secondary" onClick={handleReset}>Reset Defaults</Button>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}
